import { useState } from 'react'
import { CheckCircle2, RotateCcw, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { cn } from '@/lib/utils'
import { useRecordProgress } from './api'
import { PASSING_SCORE, type QuizQuestion } from './content'

export function Quiz({ lessonKey, questions }: { lessonKey: string; questions: QuizQuestion[] }) {
  const [answers, setAnswers] = useState<(number | null)[]>(() => questions.map(() => null))
  const [checked, setChecked] = useState(false)
  const record = useRecordProgress()
  const correct = answers.filter((answer, i) => answer === questions[i]?.answer).length
  const score = Math.round((correct / questions.length) * 100)
  const passed = score >= PASSING_SCORE

  const check = () => {
    setChecked(true)
    record.mutate(
      { lessonKey, quiz_score: score, completed: passed },
      { onError: () => toast.error('Your quiz result was not saved. Check your connection and try again.') },
    )
  }
  const retry = () => {
    setAnswers(questions.map(() => null))
    setChecked(false)
  }

  return (
    <section aria-labelledby="quiz-heading" className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="quiz-heading" className="text-base font-semibold">
          Quiz
        </h2>
        {checked && (
          <span className={cn('text-sm font-medium', passed ? 'text-emerald-700' : 'text-amber-700')} role="status">
            {correct} of {questions.length} right{passed ? ' · Lesson finished' : ` · ${PASSING_SCORE}% to pass`}
          </span>
        )}
      </div>

      <ol className="space-y-5">
        {questions.map((q, qi) => (
          <li key={q.question}>
            <fieldset>
              <legend className="mb-2 text-sm font-medium">{q.question}</legend>
              <div className="grid gap-1.5">
                {q.choices.map((choice, ci) => {
                  const chosen = answers[qi] === ci
                  const right = checked && ci === q.answer
                  const wrong = checked && chosen && ci !== q.answer
                  return (
                    <label
                      key={choice}
                      className={cn(
                        'flex min-h-10 cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50',
                        !checked && chosen && 'border-foreground bg-muted/50',
                        !checked && !chosen && 'hover:bg-muted/40',
                        right && 'border-emerald-300 bg-emerald-50 text-emerald-900',
                        wrong && 'border-red-300 bg-red-50 text-red-900',
                        checked && 'cursor-default',
                      )}
                    >
                      <input
                        type="radio"
                        name={`${lessonKey}-${qi}`}
                        className="accent-foreground"
                        checked={chosen}
                        disabled={checked}
                        onChange={() => setAnswers((current) => current.map((a, i) => (i === qi ? ci : a)))}
                      />
                      <span className="flex-1">{choice}</span>
                      {right && <CheckCircle2 className="size-4 text-emerald-600" aria-label="Right answer" />}
                      {wrong && <XCircle className="size-4 text-red-600" aria-label="Your answer" />}
                    </label>
                  )
                })}
              </div>
              {checked && <p className="mt-2 text-sm text-muted-foreground">{q.why}</p>}
            </fieldset>
          </li>
        ))}
      </ol>

      {checked ? (
        !passed && (
          <Button variant="outline" size="sm" onClick={retry}>
            <RotateCcw className="size-3.5" /> Try again
          </Button>
        )
      ) : (
        <LoadingButton
          size="sm"
          onClick={check}
          loading={record.isPending}
          loadingText="Checking..."
          disabled={answers.some((a) => a === null)}
        >
          Check answers
        </LoadingButton>
      )}
    </section>
  )
}
